import { TradeDiscountSuccessCard } from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/js-utils";

/**
 * Helper function to determine if an overlay should be registered based on trade discount success info
 *
 * @param tradeDiscountSuccessInfo - The trade discount success card data from cart response
 * @param delightPopupImpression - Whether the delight popup has already been shown in this session
 * @param registerRef - Ref to track if this specific overlay has already been registered
 * @param cardType - Optional filter for specific card type(s) to match against
 * @returns true if overlay should be registered, false otherwise
 */
export const shouldRegisterOverlay = (
    tradeDiscountSuccessInfo: TradeDiscountSuccessCard | undefined,
    delightPopupImpression: boolean,
    registerRef: React.MutableRefObject<boolean>,
    cardType?: string | string[],
): boolean => {
    // Early exit: Don't register if any of these conditions are met:
    // - No trade discount data exists
    // - Delight popup was already shown in this session (prevents duplicate popups)
    // - This specific overlay was already registered (prevents re-registration)
    if (isEmpty(tradeDiscountSuccessInfo) || delightPopupImpression || registerRef.current) {
        return false;
    }

    // Early exit: Don't register if the success card doesn't have a type
    // This ensures we have valid data to work with
    if (!tradeDiscountSuccessInfo.td_success_card_type) {
        return false;
    }

    // Case 1: Array of card types provided (e.g., GenericTDSuccessCardType = [OFFER_CARD_TYPE_REFERRAL_OFFER])
    // Used for: Generic Delight Popups that support multiple card types
    // Returns true if the current card type is in the allowed array
    if (Array.isArray(cardType)) {
        return cardType.includes(tradeDiscountSuccessInfo.td_success_card_type);
    }

    // Case 2: Single card type provided (e.g., TDSuccessCardType.OFFER_CARD_TYPE_DEAL_RUSH)
    // Used for: Specific overlays like Deal Rush
    // Returns true only if card type exactly matches
    if (cardType) {
        return tradeDiscountSuccessInfo.td_success_card_type === cardType;
    }

    // Case 3: No card type filter provided
    // Used for: PocketHero and Expiry Cashback (they have additional conditions in the caller)
    // Returns true as long as we have valid trade discount data
    // The caller will add additional checks for specific card types or reward offers
    return true;
};
